import {useState} from 'react';

import {cn} from '../../lib/utils';
import {Icon} from '../Icon/Icon';
import {Badge} from '../shadcn/badge';
import {ButtonGroup as ButtonGroupPrimitive, ButtonGroupItem} from '../shadcn/button-group';

import type {IconName} from '../../icons/icon-map';

type ButtonGroupItemWithLabel = {
  value: string;
  label: string;
  icon?: IconName;
  ariaLabel?: string;
  disabled?: boolean;
  hasError?: boolean;
  /** Shows a lock in the error indicator's slot; wins over `hasError`. */
  isLocked?: boolean;
  /** Rendered as a badge before the label — a count, total, or short status. */
  badge?: string | number;
  /** Badge tone — 'warning' calls out an actionable count (e.g. items needing attention). */
  badgeVariant?: 'outline' | 'warning';
};

type ButtonGroupItemIconOnly = {
  value: string;
  label?: never;
  icon: IconName;
  ariaLabel: string;
  disabled?: boolean;
  hasError?: boolean;
  isLocked?: never;
  badge?: never;
};

export type ButtonGroupItemDef = ButtonGroupItemWithLabel | ButtonGroupItemIconOnly;

export type ButtonGroupVariant = 'default' | 'segmented' | 'pill' | 'divided';
export type ButtonGroupSize = 'default' | 'sm';

export interface ButtonGroupProps {
  items: ButtonGroupItemDef[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  variant?: ButtonGroupVariant;
  size?: ButtonGroupSize;
}

const CONTAINER_CLASS_BY_VARIANT: Record<ButtonGroupVariant, string | undefined> = {
  default: undefined,
  segmented: 'flex w-full',
  pill: 'flex w-full gap-2 rounded-none border-none bg-transparent p-0',
  divided: 'flex w-full gap-0 overflow-hidden rounded-md border-border bg-transparent p-0 shadow-xs'
};

export function ButtonGroup({
  items,
  value: valueProp,
  defaultValue,
  onChange,
  disabled,
  hasError,
  className,
  variant = 'default',
  size = 'default'
}: ButtonGroupProps) {
  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');

  const currentValue = isControlled ? valueProp : internalValue;

  const handleValueChange = (newValues: string[]) => {
    // Prevent deselecting the current item — keep it pressed
    const next = newValues.find(v => v !== currentValue) ?? currentValue;
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  };

  const isSegmented = variant === 'segmented';
  const isPill = variant === 'pill';
  const isDivided = variant === 'divided';

  return (
    <ButtonGroupPrimitive
      disabled={disabled}
      aria-invalid={hasError || undefined}
      className={cn(CONTAINER_CLASS_BY_VARIANT[variant], className)}
      value={currentValue ? [currentValue] : []}
      onValueChange={handleValueChange}
    >
      {items.map((item, index) => (
        <ButtonGroupItem
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          size={item.label ? size : 'icon'}
          variant={isPill ? 'pill' : isDivided ? 'divided' : undefined}
          aria-label={item.ariaLabel ?? undefined}
          className={cn(
            'group/item',
            isSegmented || isPill || isDivided ? 'flex-1 justify-center' : undefined,
            isDivided && index === 0 && 'rounded-s-md',
            isDivided && index === items.length - 1 && 'rounded-e-md'
          )}
        >
          {item.icon && !item.label ? (
            <Icon name={item.icon} />
          ) : (
            <>
              {item.badge !== undefined && (
                <Badge
                  variant={item.badgeVariant === 'warning' ? 'warning' : 'outline'}
                  size="xs"
                  className={cn(
                    'shrink-0 tabular-nums',
                    item.badgeVariant !== 'warning' &&
                      'group-data-pressed/item:border-transparent group-data-pressed/item:bg-background-selected-strong group-data-pressed/item:text-brand-accent'
                  )}
                >
                  {item.badge}
                </Badge>
              )}
              {item.label}
              {item.icon && (
                <Icon
                  name={item.icon}
                  className={cn('shrink-0', size === 'sm' ? 'size-3.5' : 'size-[17px]')}
                  aria-hidden
                />
              )}
              {item.isLocked && (
                <Icon
                  name="lock"
                  data-slot="locked-indicator"
                  className="size-3.5 shrink-0 ms-1 text-fg-tertiary"
                  aria-hidden
                />
              )}
              {!item.isLocked && item.hasError && (
                <Icon
                  name="alert-circle"
                  data-slot="error-indicator"
                  className="size-3.5 shrink-0 ms-1 text-indication-error"
                  aria-hidden
                />
              )}
            </>
          )}
        </ButtonGroupItem>
      ))}
    </ButtonGroupPrimitive>
  );
}
